import type { WindowDescriptor } from '@krp/client-runtime'

import { OWN_KIND } from '../features/connection-form/model/settings'
import type { OwnUiState } from '../state'

/** Окна пакета «Собственная». Ресурсное окно — `scope: 'resource'` и `:rid` в адресе, как у остальных источников. */
export const OWN_WINDOWS = {
  sale: 'own.sale',
  /** RP-OWN3: своя номенклатура — вкладка «Номенклатуры», карточка, новый товар, группы. */
  products: 'own.products',
  product: 'own.product',
  productCreate: 'own.product-create',
  groups: 'own.groups',
  /** RP-OWN3: вкладка «Цен» — виды цен и поиск товара; цены одного товара — окном внутри раздела. */
  prices: 'own.prices',
  productPrices: 'own.product-prices',
  /** RP-OWN9: «Доставка» — что агент не доставил в центральный журнал и что требует разбора (ведомость Receipts). */
  delivery: 'own.delivery',
  /** RP-OWN11: «Кассиры» — состав кассиров собственной кассы (создать, переименовать, архив); PIN — в «Доступе кассиров». */
  cashiers: 'own.cashiers',
} as const

/** Право, по которому источник попадает в плитки и вкладку раздела «РМК» (то же, что открывает проекцию настроек). */
export const OWN_SALE_CAPABILITY = 'session.open'

/** Право чтения ведомости доставки (Resource.Receipts, `receipts.read`): то же, что открывает журнал чеков ресурса. */
export const OWN_DELIVERY_CAPABILITY = 'receipts.read'

/** Право вести кассиров (Resource.Receipts, `receipts.cashiers.manage`): у системного администратора есть, в ролях по умолчанию нет. */
export const OWN_CASHIERS_CAPABILITY = 'receipts.cashiers.manage'

/** Права своей номенклатуры — те же имена, что в манифесте вида `own` и в `OwnCapabilities` службы. */
export const OWN_CATALOG_CAPABILITIES = {
  catalogRead: 'catalog.read',
  catalogWrite: 'catalog.write',
  pricesRead: 'prices.read',
  pricesWrite: 'prices.write',
} as const

/** Разделы меню Platform (UI-SECTIONS): имена сверяет реестр окон при сборке приложения. */
const SECTIONS = { nomenclature: 'nomenclature', prices: 'prices', receipts: 'receipts' } as const

export const ownWindows: readonly WindowDescriptor<OwnUiState>[] = [
  {
    kind: OWN_WINDOWS.sale,
    scope: 'resource',
    paths: ['/own/:rid/sale'],
    label: 'РМК',
    section: { id: 'rmk', tab: { resourceKind: OWN_KIND, capability: OWN_SALE_CAPABILITY, label: 'РМК', order: 10 } },
  },
  {
    kind: OWN_WINDOWS.products,
    scope: 'resource',
    paths: ['/own/:rid/products'],
    label: 'Товары своей базы',
    // После 1С и кабинетов AQSI: своя база — третий источник раздела.
    section: { id: SECTIONS.nomenclature, tab: { resourceKind: OWN_KIND, capability: OWN_CATALOG_CAPABILITIES.catalogRead, label: 'Товары', order: 30 } },
  },
  {
    kind: OWN_WINDOWS.product,
    scope: 'resource',
    paths: ['/own/:rid/products/:id'],
    label: 'Товар своей базы',
    size: 'lg',
    source: { id: 'own', label: 'Свой' },
    section: { id: SECTIONS.nomenclature },
  },
  {
    kind: OWN_WINDOWS.productCreate,
    scope: 'resource',
    paths: ['/own/:rid/product-create'],
    label: 'Новый товар своей базы',
    size: 'lg',
    source: { id: 'own', label: 'Свой' },
    section: { id: SECTIONS.nomenclature },
  },
  {
    kind: OWN_WINDOWS.groups,
    scope: 'resource',
    paths: ['/own/:rid/groups'],
    label: 'Группы своей базы',
    section: { id: SECTIONS.nomenclature },
  },
  {
    kind: OWN_WINDOWS.prices,
    scope: 'resource',
    paths: ['/own/:rid/prices'],
    label: 'Цены своей базы',
    section: { id: SECTIONS.prices, tab: { resourceKind: OWN_KIND, capability: OWN_CATALOG_CAPABILITIES.pricesRead, label: 'Цены', order: 30 } },
  },
  {
    kind: OWN_WINDOWS.productPrices,
    scope: 'resource',
    paths: ['/own/:rid/prices/:id'],
    label: 'Цены товара своей базы',
    size: 'lg',
    section: { id: SECTIONS.prices },
  },
  {
    kind: OWN_WINDOWS.delivery,
    scope: 'resource',
    paths: ['/own/:rid/delivery'],
    label: 'Доставка в журнал',
    size: 'lg',
    section: { id: SECTIONS.receipts },
  },
  {
    kind: OWN_WINDOWS.cashiers,
    scope: 'resource',
    paths: ['/own/:rid/cashiers'],
    label: 'Кассиры',
    size: 'lg',
    section: { id: SECTIONS.receipts },
  },
]
